/**
 * Check if a discount/rebate item is expired based on its expires field
 * @param {Object} item - Discount or rebate with optional expires field (YYYY-MM-DD)
 * @returns {boolean} true if expired
 */
export const isItemExpired = (item, isSigned = false) => {
  if (isSigned) return false;
  if (!item?.expires) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiresDate = new Date(item.expires + 'T23:59:59');
  return expiresDate < today;
};

/**
 * Check if a discount/rebate is active (selected).
 * Expiration is visual-only — expired items still count toward price.
 */
export const isItemActive = (item) => {
  return !!item?.isSelected;
};

/**
 * Calculate total equipment price from proposalSystems
 */
export const calculateEquipmentTotal = (proposal) => {
  if (!proposal) return 0;

  if (proposal.proposalSystems && proposal.proposalSystems.length > 0) {
    const systems = proposal.optionAccepted && proposal.selectedOptionId
      ? proposal.proposalSystems.filter(s => s.optionId === proposal.selectedOptionId)
      : proposal.proposalSystems;

    return systems.reduce((total, system) => {
      const equipmentTotal = (system.equipments || []).reduce(
        (sum, eq) => sum + (Number(eq.priceAtCreation) || 0),
        0
      );
      return total + equipmentTotal;
    }, 0);
  }

  return 0;
};

/**
 * Calculate total scope of work price from proposal.scopeOfWorks
 */
export const calculateScopeOfWorkTotal = (proposal) => {
  if (!proposal) return 0;

  // Contract level: proposal.scopeOfWorks
  if (proposal.scopeOfWorks && proposal.scopeOfWorks.length > 0) {
    return proposal.scopeOfWorks.reduce(
      (sum, w) => sum + (Number(w.price) || 0),
      0
    );
  }

  // Fallback: aggregate from proposalSystems (filtered by accepted option)
  if (proposal.proposalSystems && proposal.proposalSystems.length > 0) {
    const systems = proposal.optionAccepted && proposal.selectedOptionId
      ? proposal.proposalSystems.filter(s => s.optionId === proposal.selectedOptionId)
      : proposal.proposalSystems;

    return systems.reduce((total, system) => {
      const sowTotal = (system.scopeOfWorks || []).reduce(
        (sum, w) => sum + (Number(w.price) || 0),
        0
      );
      return total + sowTotal;
    }, 0);
  }

  return 0;
};

/**
 * Calculate total work description price for Service proposals
 */
export const calculateWorkDescriptionTotal = (proposal) => {
  if (!proposal) return 0;

  if (proposal.workDescriptions && proposal.workDescriptions.length > 0) {
    return proposal.workDescriptions.reduce(
      (sum, w) => sum + (Number(w.price) || 0),
      0
    );
  }

  return 0;
};

/**
 * Calculate total warranty price from proposal.warranties
 */
export const calculateWarrantyTotal = (proposal) => {
  if (!proposal) return 0;

  // Contract level: proposal.warranties
  if (proposal.warranties && proposal.warranties.length > 0) {
    return proposal.warranties.reduce(
      (sum, w) => sum + (Number(w.price) || 0),
      0
    );
  }

  // Fallback: aggregate from proposalSystems (filtered by accepted option)
  if (proposal.proposalSystems && proposal.proposalSystems.length > 0) {
    const systems = proposal.optionAccepted && proposal.selectedOptionId
      ? proposal.proposalSystems.filter(s => s.optionId === proposal.selectedOptionId)
      : proposal.proposalSystems;

    return systems.reduce((total, system) => {
      const warrantyTotal = (system.warranties || []).reduce(
        (sum, w) => sum + (Number(w.price) || 0),
        0
      );
      return total + warrantyTotal;
    }, 0);
  }

  return 0;
};

/**
 * Calculate total price based on proposal type
 * Installation: equipment + installation + scope of work + warranty
 * Service: work description + job price + warranty
 */
export const calculateTotalPrice = (proposal) => {
  if (!proposal) return 0;

  // Service Proposal calculation
  if (proposal.proposalType === 'Service') {
    const workDescriptionTotal = calculateWorkDescriptionTotal(proposal);
    const jobPrice = Number(proposal.jobPrice) || 0;
    const warrantyTotal = calculateWarrantyTotal(proposal);
    return workDescriptionTotal + jobPrice + warrantyTotal;
  }

  // Installation Proposal calculation (default)
  const equipmentTotal = calculateEquipmentTotal(proposal);
  const installationPrice = Number(proposal.installationPrice) || 0;
  const scopeOfWorkTotal = calculateScopeOfWorkTotal(proposal);
  const warrantyTotal = calculateWarrantyTotal(proposal);

  return equipmentTotal + installationPrice + scopeOfWorkTotal + warrantyTotal;
};

/**
 * Get installation price from proposal level
 */
export const calculateTotalInstallationPrice = (proposal) => {
  if (!proposal) return 0;
  return Number(proposal.installationPrice) || 0;
};

/**
 * Calculate total rebates from proposal.contractRebates
 */
export const calculateTotalRebates = (proposal) => {
  if (!proposal) return 0;

  if (proposal.contractRebates && proposal.contractRebates.length > 0) {
    return proposal.contractRebates
      .filter((r) => isItemActive(r))
      .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  }

  return proposal.data?.rebates || 0;
};

/**
 * Calculate total discounts (instants) from proposal.discounts
 */
export const calculateTotalInstants = (proposal) => {
  if (!proposal) return 0;

  if (proposal.discounts && proposal.discounts.length > 0) {
    return proposal.discounts
      .filter((d) => isItemActive(d))
      .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
  }

  return proposal.data?.instants || 0;
};

/**
 * Calculate total after rebates and instants
 * Installation: Total Price - Rebates - Discounts
 * Service: Total Price - Discounts (no rebates)
 */
export const calculateTotalAfter = (proposal) => {
  if (!proposal) return 0;

  const totalPrice = calculateTotalPrice(proposal);
  const instants = calculateTotalInstants(proposal);

  // Service proposals don't have rebates
  if (proposal.proposalType === 'Service') {
    return totalPrice - instants;
  }

  // Installation proposals have rebates
  const rebates = calculateTotalRebates(proposal);
  return totalPrice - rebates - instants;
};

/**
 * Calculate total to pay (after rebates and instants)
 * Dynamic calculation is preferred (accurate when full relations are loaded).
 * Falls back to the persisted totalPay column (for list views / search).
 */
export const calculateTotalPay = (proposal) => {
  const calculated = calculateTotalAfter(proposal);
  if (calculated > 0) return calculated;
  if (proposal?.totalPay > 0) return Number(proposal.totalPay);
  if (proposal?.data?.totalPay) return Number(proposal.data.totalPay) || 0;
  return 0;
};

/**
 * Calculate equipment total for a specific option
 */
export const calculateEquipmentTotalForOption = (proposal, optionId) => {
  if (!proposal?.proposalSystems || !optionId) return 0;
  return proposal.proposalSystems
    .filter(s => s.optionId === optionId)
    .reduce((total, system) => {
      const equipmentTotal = (system.equipments || []).reduce(
        (sum, eq) => sum + (Number(eq.priceAtCreation) || 0),
        0
      );
      return total + equipmentTotal;
    }, 0);
};

/**
 * Calculate total price for a specific option.
 * Replaces equipment total with the selected option's equipment,
 * keeping installation, scope of work, warranty unchanged.
 */
export const calculateTotalPriceForOption = (proposal, optionId) => {
  if (!proposal || !optionId) return calculateTotalPrice(proposal);

  const equipmentTotal = calculateEquipmentTotalForOption(proposal, optionId);
  const installationPrice = Number(proposal.installationPrice) || 0;
  const scopeOfWorkTotal = calculateScopeOfWorkTotal(proposal);
  const warrantyTotal = calculateWarrantyTotal(proposal);

  return equipmentTotal + installationPrice + scopeOfWorkTotal + warrantyTotal;
};

/**
 * Calculate total to pay for a specific option (after rebates and discounts)
 */
export const calculateTotalPayForOption = (proposal, optionId) => {
  if (!proposal || !optionId) return calculateTotalPay(proposal);

  const totalPrice = calculateTotalPriceForOption(proposal, optionId);
  const rebates = calculateTotalRebates(proposal);
  const instants = calculateTotalInstants(proposal);

  const calculated = totalPrice - rebates - instants;
  if (calculated < 0) return 0;
  return calculated;
};

/**
 * Calculate total deposit amount from payments marked as deposit
 */
export const calculateTotalDeposit = (proposal) => {
  if (!proposal?.payments || proposal.payments.length === 0) return 0;
  
  return proposal.payments
    .filter(p => p.isDeposit === true)
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
};
